import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, axiosClient, useAuth } from '../../auth-lib';
import { Button, Modal, Select, Skeleton, StatusPill, useToast } from '../../design-system';
import TodoTimeModal from './TodoTimeModal';
import TodoEditModal from './TodoEditModal';
import TodoDetailModal from './TodoDetailModal';
import { formatMinutes, joinMinutes, totalLogged } from './HoursMinutesInput';
import '../styles/Todos.css';
import { formatTaskNumber } from '../../lib/taskRef';
import { invalidateNavCounts } from '../../lib/navCounts';



// Short "13 Jul" style date for the created / completed stamps on each row.
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null);

// Search matches the to-do's own title, and — for task-linked ones — the task
// it belongs to. Task IDs are matched flexibly, so "TASK-0024", "task-24",
// "0024" and plain "24" all find TASK-0024 (people rarely type the padding).
const matchesSearch = (todo, term) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  if (todo.title?.toLowerCase().includes(q)) return true;

  const task = todo.taskId;
  if (!task) return false;
  if (task.title?.toLowerCase().includes(q)) return true;
  if (task.taskNumber == null) return false;

  if (formatTaskNumber(task.taskNumber).toLowerCase().includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  return digits !== '' && Number(digits) === task.taskNumber;
};

// Same status set the task/subtask UI uses, so a to-do reads the same.
const STATUS_OPTIONS = [
  { value: 'todo', label: 'Created' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'waiting-client', label: 'Waiting on Client' },
  { value: 'completed', label: 'Completed' },
  { value: 'on-hold', label: 'On Hold' },
];

// Same four levels as a task, so a to-do sorts and reads the same way.
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

const AVATAR_COLORS = ['#4f6f5c', '#9c5b3e', '#b08428', '#2b3a4a', '#6e6b66', '#7a6a9c'];

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
}

function Avatar({ name }) {
  const color = AVATAR_COLORS[(name?.length || 0) % AVATAR_COLORS.length];
  return <span className="todo-avatar" style={{ background: color }} title={name}>{initials(name)}</span>;
}

const TrashSm = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4.5 7h15M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M7 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4L17 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const PencilSm = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 20h4l10-10a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5 4 20Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const ClockSm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" /><path d="M12 7.5V12l3 1.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// Reusable to-do list, in four scopes:
//   'task'       — to-dos attached to one task (a task's To-Do tab)
//   'standalone' — only the caller's own personal to-dos
//   'mine'       — the caller's personal list: created OR assigned to them,
//                  standalone AND task-linked (the employee To-Dos page)
//   'all'        — every to-do in the org, personal AND task-linked, each
//                  labelled with where it came from (the To-Do nav page)
// Optional knobs let the employee reuse this with its own rules:
//   taskLinkBase   — where an origin chip navigates (task detail route)
//   assigneeFilter — narrow the assignee dropdown (e.g. delegate below my level)
//   taskPool       — tasks a NEW to-do may be linked to (adds a picker on create)
//   defaultAssignee— pre-selected assignee for new to-dos ('' = Unassigned)
function TodoList({
  taskId = null,
  scope = taskId ? 'task' : 'standalone',
  taskLinkBase = '/org-admin/tasks',
  assigneeFilter = null,
  taskPool = null,
  defaultAssignee = '',
  enableDetailModal = false,
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = user?._id || user?.id;
  const myLevel = user?.role?.level ?? 0;
  const [todos, setTodos] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [remark, setRemark] = useState(''); // optional note, set at creation
  const [assignee, setAssignee] = useState(defaultAssignee);
  const [priority, setPriority] = useState('medium');
  const [linkTaskId, setLinkTaskId] = useState(''); // link a new to-do to a task
  const [allocH, setAllocH] = useState(''); // allocated time, optional
  const [allocM, setAllocM] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('active'); // 'active' | 'deleted'
  const [origin, setOrigin] = useState('all'); // 'all' | 'general' | 'task'
  const [search, setSearch] = useState('');
  const [timeTodo, setTimeTodo] = useState(null); // to-do whose time panel is open
  const [editTodo, setEditTodo] = useState(null); // to-do whose edit modal is open
  const [detailTodo, setDetailTodo] = useState(null); // to-do whose read-only detail is open
  const [confirmComplete, setConfirmComplete] = useState(null); // to-do awaiting completion confirmation
  const [completing, setCompleting] = useState(false);

  const showOrigin = scope === 'all' || scope === 'mine';

  useEffect(() => {
    fetchTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, scope, view]);

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
  }, []);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      // No taskId and no standalone flag => the API returns the whole org's
      // to-dos, which is what the 'all' scope wants.
      const params = {};
      if (scope === 'task') params.taskId = taskId;
      else if (scope === 'mine') params.mine = 'true';
      else if (scope === 'standalone') params.standalone = 'true';
      if (view === 'deleted') params.deleted = 'true';
      const res = await apiClient.get('/api/todos', { params });
      setTodos(res.data);
    } catch (err) {
      toast.danger('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  };

  const visibleTodos = useMemo(() => {
    let list = todos;
    if (showOrigin && origin !== 'all') {
      list = list.filter((t) => (origin === 'task' ? !!t.taskId : !t.taskId));
    }
    if (search.trim()) list = list.filter((t) => matchesSearch(t, search));
    // Urgent first, then by due date — an unprioritised list of 60 to-dos
    // doesn't answer "what next?". Completed ones sink regardless.
    return [...list].sort((a, b) => {
      if ((a.status === 'completed') !== (b.status === 'completed')) {
        return a.status === 'completed' ? 1 : -1;
      }
      const pr = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
      if (pr !== 0) return pr;
      if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      return 0;
    });
  }, [todos, origin, showOrigin, search]);

  const counts = useMemo(() => ({
    all: todos.length,
    general: todos.filter((t) => !t.taskId).length,
    task: todos.filter((t) => !!t.taskId).length,
  }), [todos]);

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setSaving(true);
    try {
      const res = await apiClient.post('/api/todos', {
        title: t,
        remark: remark.trim() || undefined,
        assignee: assignee || undefined,
        priority,
        // Inside a task tab the task is fixed; otherwise honour the picker.
        taskId: taskId || linkTaskId || undefined,
        allocatedMinutes: joinMinutes(allocH, allocM) ?? undefined,
      });
      setTodos((prev) => [res.data, ...prev]);
      // ⚠️ The sidebar's to-do pill is a module-level cache no page can see
      // into. Four writes in this file move that number, and this component is
      // rendered on five different screens — invalidating here rather than in
      // each of them is the only way they cannot drift apart.
      invalidateNavCounts();
      setTitle('');
      setRemark('');
      setAssignee(defaultAssignee);
      setPriority('medium');
      setLinkTaskId('');
      setAllocH('');
      setAllocM('');
    } catch (err) {
      toast.danger('Failed to add to-do');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (todo, status) => {
    const prev = todos;
    setTodos((list) => list.map((t) => (t._id === todo._id ? { ...t, status } : t)));
    try {
      const res = await apiClient.put(`/api/todos/${todo._id}`, { status });
      setTodos((list) => list.map((t) => (t._id === todo._id ? res.data : t)));
      invalidateNavCounts();
    } catch (err) {
      setTodos(prev);
      toast.danger(err.response?.data?.error || 'Failed to update to-do');
    }
  };

  // Marking a to-do 'completed' is irreversible, so it goes through a
  // confirmation step; every other status change applies immediately.
  const onStatusSelect = (todo, status) => {
    if (status === todo.status) return;
    if (status === 'completed') {
      setConfirmComplete(todo);
      return;
    }
    setStatus(todo, status);
  };

  const confirmCompletion = async () => {
    if (!confirmComplete) return;
    setCompleting(true);
    await setStatus(confirmComplete, 'completed');
    setCompleting(false);
    setConfirmComplete(null);
  };

  const remove = async (todo) => {
    const prev = todos;
    setTodos((list) => list.filter((t) => t._id !== todo._id));
    try {
      await apiClient.delete(`/api/todos/${todo._id}`);
      invalidateNavCounts();
    } catch (err) {
      setTodos(prev);
      toast.danger('Failed to delete to-do');
    }
  };

  const restore = async (todo) => {
    const prev = todos;
    setTodos((list) => list.filter((t) => t._id !== todo._id));
    try {
      await apiClient.patch(`/api/todos/${todo._id}/restore`);
      invalidateNavCounts();
    } catch (err) {
      setTodos(prev);
      toast.danger('Failed to restore to-do');
    }
  };

  // Mirror the server's canManageTodo rule so the UI only offers what will be
  // allowed: the owner or management above the subject may edit/delete; the
  // assignee may only change status (and log time); everyone else is read-only.
  const rowPerms = (todo) => {
    const subjectLevel = todo.assignee?.role?.level ?? todo.createdBy?.role?.level ?? 0;
    const isOwner = (todo.createdBy?._id || todo.createdBy) === myId;
    const isAssignee = (todo.assignee?._id || todo.assignee) === myId;
    const canManage = isOwner || myLevel > subjectLevel;
    return { canManage, canStatus: canManage || isAssignee };
  };

  const isDeletedView = view === 'deleted';

  return (
    <div className="todolist">
      <div className="todolist-topbar">
        <input
          className="todolist-search"
          placeholder="🔎 Search to-dos or TASK-0024…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="todolist-toggle">
          <button type="button" className={!isDeletedView ? 'is-active' : ''} onClick={() => setView('active')}>Active</button>
          <button type="button" className={isDeletedView ? 'is-active' : ''} onClick={() => setView('deleted')}>Deleted</button>
        </div>

        {showOrigin && (
          <div className="todolist-toggle">
            <button type="button" className={origin === 'all' ? 'is-active' : ''} onClick={() => setOrigin('all')}>
              All ({counts.all})
            </button>
            <button type="button" className={origin === 'general' ? 'is-active' : ''} onClick={() => setOrigin('general')}>
              General ({counts.general})
            </button>
            <button type="button" className={origin === 'task' ? 'is-active' : ''} onClick={() => setOrigin('task')}>
              Task-linked ({counts.task})
            </button>
          </div>
        )}
      </div>

      {!isDeletedView && (
        <div className={`todolist-add ${scope === 'mine' ? 'todolist-add-emp' : ''}`}>
          <input
            className="todolist-input"
            placeholder="Add a to-do…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
          {/* Optional remark, inline between the title and the assignee. */}
          <input
            className="todolist-input todolist-remark-input"
            placeholder="Remark (optional)…"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
          {/* Link the new to-do to a task (only where a pool is supplied and
              we're not already inside a task's To-Do tab). */}
          {taskPool && scope !== 'task' && (
            <Select
              value={linkTaskId}
              onChange={(e) => setLinkTaskId(e.target.value)}
              className="todolist-tasklink"
              title="Link this to-do to one of your tasks (optional)"
            >
              <option value="">General (no task)</option>
              {taskPool.map((t) => (
                <option key={t._id} value={t._id}>
                  {formatTaskNumber(t.taskNumber)} · {t.title}
                </option>
              ))}
            </Select>
          )}
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="todolist-priority"
            title="Priority"
          >
            {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
          <Select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="todolist-assignee">
            <option value="">Unassigned</option>
            {(assigneeFilter ? orgUsers.filter(assigneeFilter) : orgUsers).map((u) => (
              <option key={u._id} value={u._id}>{u.name}</option>
            ))}
          </Select>
          {/* Optional time budget — units shown inside the boxes (hh : mm). */}
          <div className="todolist-alloc" title="Allocated time (optional)">
            <input
              className="todolist-input hm-num"
              type="number"
              min="0"
              placeholder="hh"
              aria-label="Allocated hours"
              value={allocH}
              onChange={(e) => setAllocH(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            />
            <span className="hm-sep">:</span>
            <input
              className="todolist-input hm-num"
              type="number"
              min="0"
              max="59"
              placeholder="mm"
              aria-label="Allocated minutes"
              value={allocM}
              onChange={(e) => setAllocM(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            />
          </div>
          <Button variant="primary" size="sm" onClick={add} loading={saving}>Add</Button>
        </div>
      )}

      {loading ? (
        <div className="todolist-items">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={22} style={{ marginBottom: 12 }} />)}
        </div>
      ) : visibleTodos.length === 0 ? (
        <div className="todo-empty">
          <div className="todo-empty-icon">{search.trim() ? '🔎' : (isDeletedView ? '🗑️' : '📝')}</div>
          <div className="todo-empty-text">
            {search.trim()
              ? `No to-dos match “${search.trim()}”`
              : isDeletedView ? 'No deleted to-dos' : 'There are no To-Dos'}
          </div>
        </div>
      ) : (
        <div className="todolist-items">
          {visibleTodos.map((todo) => {
            const perms = rowPerms(todo);
            return (
            <div key={todo._id} className={`todo-row ${showOrigin ? 'has-origin' : ''} ${todo.status === 'completed' ? 'is-done' : ''} ${isDeletedView ? 'is-deleted' : ''}`}>
              {/* Title and its remark share the first column, stacked. In the
                  admin list this area is clickable to open the full detail. */}
              <div
                className={`todo-main ${enableDetailModal ? 'is-clickable' : ''}`}
                role={enableDetailModal ? 'button' : undefined}
                tabIndex={enableDetailModal ? 0 : undefined}
                title={enableDetailModal ? 'View to-do details' : undefined}
                onClick={enableDetailModal ? () => setDetailTodo(todo) : undefined}
                onKeyDown={
                  enableDetailModal
                    ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailTodo(todo); } }
                    : undefined
                }
              >
                <span className="todo-title">
                  {todo.title}
                  {todo.comments?.length > 0 && (
                    <span className="todo-commentcount" title={`${todo.comments.length} comment(s)`}>
                      💬 {todo.comments.length}
                    </span>
                  )}
                </span>
                {todo.remark && (
                  <span className="todo-remark" title={todo.remark}>💬 {todo.remark}</span>
                )}
                <span className="todo-dates">
                  Created {fmtDate(todo.createdAt)}
                  {todo.completedAt && (
                    <span className="todo-dates-done"> · Completed {fmtDate(todo.completedAt)}</span>
                  )}
                </span>
              </div>

              {/* Where this to-do came from: a standalone/personal one, or one
                  raised inside a task (click through to that task). */}
              {showOrigin && (
                todo.taskId ? (
                  <button
                    type="button"
                    className="todo-origin todo-origin-task"
                    title={todo.taskId.title || 'Open task'}
                    onClick={() => navigate(`${taskLinkBase}/${todo.taskId._id}`)}
                  >
                    {formatTaskNumber(todo.taskId.taskNumber)}
                  </button>
                ) : (
                  <span className="todo-origin todo-origin-general" title="A standalone to-do, not tied to any task">
                    General
                  </span>
                )
              )}

              {/* Priority sits next to the status so the row answers "how
                  urgent" and "how far along" in one glance. */}
              {!isDeletedView && <StatusPill status={todo.priority || 'medium'} />}

              {isDeletedView ? (
                <>
                  <span className="todo-deleted-note">Deleted</span>
                  {todo.assignee?.name ? <Avatar name={todo.assignee.name} /> : <span className="todo-noassignee">—</span>}
                  {perms.canManage && <button className="todo-restore" onClick={() => restore(todo)}>Restore</button>}
                </>
              ) : (
                <>
                  {/* Log / view time — spent (/ allocated); turns red over budget. */}
                  <button
                    type="button"
                    className={`todo-time-chip ${
                      todo.allocatedMinutes != null && totalLogged(todo) > todo.allocatedMinutes ? 'is-over' : ''
                    }`}
                    onClick={() => setTimeTodo(todo)}
                    title="Log time / view time breakdown"
                  >
                    <ClockSm />
                    <span>
                      {formatMinutes(totalLogged(todo))}
                      {todo.allocatedMinutes != null && ` / ${formatMinutes(todo.allocatedMinutes)}`}
                    </span>
                  </button>
                  {/* Once completed the status is frozen (see backend guard) —
                      lock the control so it can't be reopened. */}
                  <Select
                    value={todo.status}
                    onChange={(e) => onStatusSelect(todo, e.target.value)}
                    className="todo-status"
                    disabled={todo.status === 'completed' || !perms.canStatus}
                    title={
                      todo.status === 'completed'
                        ? 'Completed to-dos cannot be reopened'
                        : !perms.canStatus
                          ? 'Only the assignee or management can change this'
                          : undefined
                    }
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  {todo.assignee?.name ? <Avatar name={todo.assignee.name} /> : <span className="todo-noassignee">—</span>}
                  {/* Editing the content and deleting are the owner's / upper
                      management's call — hidden for a plain assignee, who can
                      still progress the status and log time above. */}
                  {perms.canManage && (
                    <>
                      <button className="todo-edit" onClick={() => setEditTodo(todo)} aria-label="Edit to-do" title="Edit title, remark, assignee, budget"><PencilSm /></button>
                      <button className="todo-del" onClick={() => remove(todo)} aria-label="Delete to-do"><TrashSm /></button>
                    </>
                  )}
                </>
              )}
            </div>
            );
          })}
        </div>
      )}

      {timeTodo && (
        <TodoTimeModal
          todo={timeTodo}
          orgUsers={orgUsers}
          canEditAllocation={rowPerms(timeTodo).canManage}
          onClose={() => setTimeTodo(null)}
          onUpdated={(updated) => {
            // Keep both the list row and the open modal in sync.
            setTodos((list) => list.map((t) => (t._id === updated._id ? updated : t)));
            setTimeTodo(updated);
          }}
        />
      )}

      {editTodo && (
        <TodoEditModal
          todo={editTodo}
          orgUsers={orgUsers}
          assigneeFilter={assigneeFilter}
          onClose={() => setEditTodo(null)}
          onUpdated={(updated) => {
            setTodos((list) => list.map((t) => (t._id === updated._id ? updated : t)));
          }}
        />
      )}

      {detailTodo && (
        <TodoDetailModal
          todo={detailTodo}
          onClose={() => setDetailTodo(null)}
          onUpdated={(updated) => {
            // Keep the row and the open modal in step, same as the time modal.
            setTodos((list) => list.map((t) => (t._id === updated._id ? updated : t)));
            setDetailTodo(updated);
          }}
        />
      )}

      <Modal
        isOpen={!!confirmComplete}
        onClose={() => (completing ? null : setConfirmComplete(null))}
        title="Mark as completed?"
        size="sm"
        footer={(
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmComplete(null)} disabled={completing}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={confirmCompletion} loading={completing}>
              Yes, complete it
            </Button>
          </>
        )}
      >
        <p className="todo-confirm-text">
          Do you want to mark <strong>“{confirmComplete?.title}”</strong> as completed?
        </p>
        <p className="todo-confirm-warn">
          ⚠️ This can’t be reversed — a completed to-do cannot be reopened.
        </p>
      </Modal>
    </div>
  );
}

export default TodoList;
